/**
 * Export Jurisdiction and Classification U.S. EAR99, India Not Subject to Regulations
 */

import { useForm } from 'react-hook-form';
import { MessageType, type BaseCallBackInterface } from '../../../interfaces/baseInterface';
import CustomFormContainer from '../CustomFormContainer/CustomFormContainer';
import FormLayout from '../FormLayout/FormLayout';
import styles from './CallbasedViewGenerator.module.css';
import React, { useEffect, useRef, useState, type JSX } from 'react';
import { AttributeInputCallback, CallbackType, FRAuth, FRStep, PollingWaitCallback, StepType, } from '@forgerock/javascript-sdk';
import { fillStep, getTextOutputCallBackByMessageType } from '../../../utils/FRCallBackUtil';
import { useTranslation } from '../Translation/translationProvider';
import {  getPolicyByPolicyID, getUiElementBasedOnCallBack } from '../../../utils/uiHelper';
import { cloneDeepWith, isEmpty } from 'lodash';
import Notification from '../Notification/Notification';
import FormSection from '../FormSection/FormSection';
import Loader from '../Loader/Loader';
import { useAppDispatch } from '../../../redux/hooks';
import { setMaintenanceEnabled, setMaintenanceMessage } from '../../../redux/loginSlice';


interface CallbasedViewGeneratorProps extends BaseCallBackInterface {
  additionalBottomElement?: JSX.Element;
  onClickFunctionMap?: Map<string, Function>;
  isSubmitHidden?:boolean
}

const viewableCallBackTypes = [
  "BooleanAttributeInputCallback",
  "NameCallback",
  "PasswordCallback",
  "StringAttributeInputCallback",
  "NumberAttributeInputCallback",
  "ValidatedCreatePasswordCallback",
  "TermsAndConditionsCallback",
];


/**
 * This component generates the UI for a given step in a forgerock journey
 * The component uses react-hook-form library to generate the form elements
 * The component will render a form based on the callbacks recieved from forgerock
 * The component will also render a notification if there is an error or an
 * info message from the forgerock journey
 * The component will also render a back button if the backLink prop is provided
 * The component will also render a static text at the top of the form if the 
 * staticText prop is provided
 * The component will also render a button at the bottom of the form if the button
 * prop is provided
 * The component will also render an additional bottom element if the 
 * additionalBottomElement prop is provided
 * The component will also render a loading indicator if the journey is in progress
 * The component will also handle the next step in the journey after the form is
 * submitted
 * @param {Object} props - The props object
 * @param {Object} props.tree - The tree object from the forgerock config
 * @param {Object} props.step - The current step object from the forgerock journey
 * @param {Function} props.setStep - The function to set the next step in the
 * journey
 * @param {String} [props.backLink] - The link to go back to the previous step
 * @param {JSX.Element} [props.additionalBottomElement] - The additional element
 * to render at the bottom of the form
 * @param {Map} [props.onClickFunctionMap] - The map of functions to call when a
 * callback is clicked
 * @param {String} [props.message] - The message to display in the notification
 * @param {MessageType} [props.messageType] - The type of the message to display
 * in the notification
 * @param {Boolean} [props.isAlertOpen] - Whether or not the notification is open
 * @param {Function} [props.setIsAlertOpen] - The function to call when the
 * notification is opened or closed
 */
const CallbasedViewGenerator: React.FC<CallbasedViewGeneratorProps> = (props) => {
  const formContext = useForm<{}>({
    defaultValues: {},
  });
  const { t } = useTranslation();
  const { tree, step, setStep,
    backLink, additionalBottomElement,
    onClickFunctionMap, setMessage, setMessageType,
    message, messageType, isAlertOpen, setIsAlertOpen,isSubmitHidden } = props;

    const dispatch = useAppDispatch();

  const [isPollWaitTimerRegistered, setIsPollWaitTimerRegistered] = useState(false);
   const [loaderVisible, isLoaderVisible] = useState(false);


  const timerIdRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
      }
    };
  }, []);

  /**All the useState are listed below */
  const [isDisabled, setDisabled] = useState(false);
  const [bottomElement, setBottomElement] = useState<React.ReactNode>();
  const [backBottomElement, setBackBottomElement] = useState<React.ReactNode>();
  const [metaDataMap, setMetaDataMap] = useState(new Map());


  const setInfoErrorMessage = () => {
    let messageType: MessageType;
    step.getCallbacksOfType(CallbackType.TextOutputCallback).forEach(textCallback => {
      switch (textCallback.getOutputByName<string>('messageType', '')) {
        case "2":
          messageType = MessageType.error
          break;
        case "1":
          messageType = MessageType.info;
          break;
        default:
          return;
      }
      if (messageType) {
        setMessageType ? setMessageType(MessageType.error) : null;
        setMessage ? setMessage(textCallback.getOutputByName('message', '')) : null
        setIsAlertOpen ? setIsAlertOpen(true) : null
      }
    })      
  }
  useEffect(() => {
    if (!!step && step.type === StepType.Step) {
      if (step.getCallbacksOfType(CallbackType.MetadataCallback).length) {
        const localMetaDataMap = new Map(
          Object.entries(JSON.parse(JSON.stringify(step.getCallbackOfType(CallbackType.MetadataCallback)?.getOutputByName("data", {}))))
        );
        setMetaDataMap(localMetaDataMap);
        const $maintenance= localMetaDataMap.get("$maintenance") as {isLoginDisabled: boolean,message: string,appMaintenance:boolean};
        $maintenance?.isLoginDisabled  ? setDisabled(true) : setDisabled(false);

        dispatch(setMaintenanceMessage($maintenance?.message || ''))
        dispatch(setMaintenanceEnabled($maintenance?.appMaintenance || false))
        createBottomElement(localMetaDataMap)
      }else{
        createBottomElement()
      }
      
      
      setInfoErrorMessage()
    }
    //To check Password history
    if (step.getCallbacksOfType(CallbackType.ValidatedCreatePasswordCallback).length){
    const validatePasswordCallbacks = step.getCallbacksOfType(CallbackType.ValidatedCreatePasswordCallback);
    if (validatePasswordCallbacks?.length) {
      validatePasswordCallbacks.forEach((validatePasswordCallback) => {
        const attributeInputCallback = new AttributeInputCallback(validatePasswordCallback.payload);
        const failedPolicies = attributeInputCallback.getFailedPolicies();
        if (failedPolicies?.length) {
          failedPolicies.forEach((failedPolicy: { policyRequirement: string }) => {
            if (failedPolicy.policyRequirement === "PASSWORD_HISTORY") {
              const errorMessage = getPolicyByPolicyID(attributeInputCallback, "UI-TYPE", "passwordHistory");
              if (errorMessage && errorMessage.trim()) {
                setIsAlertOpen?.(true);
                setMessageType?.(MessageType.error);
                setMessage?.(errorMessage);
              }
            }
          });
        }
      });
    }
  }
  }, [step,isSubmitHidden]);


  const getUIElementsFromMap = (metaInfoMap: Map<any, JSX.Element[]>) => {
    let uiElements: JSX.Element[] = [];
    metaInfoMap.forEach((value, key) => {
      if (metaDataMap.get("info$" + key)) {
        uiElements.push(
          <FormSection key={key} sectionHeader={key} infoData={metaDataMap.get("info$" + key).tooltip}>
            {value}
          </FormSection>
        );
      } else {
        uiElements.push(
          <FormSection key={key} sectionHeader={key}>
            {value}
          </FormSection>
        );
      }
    });
    return uiElements;
  };


  const handlePollingWaitCallBack = (callback: PollingWaitCallback) => {
    
    if (!isPollWaitTimerRegistered) {
      isLoaderVisible(true)
      setTimeout(async () => {
        setMessage ? setMessage("") : null
         setIsAlertOpen ? setIsAlertOpen(false) : null
        const nextstep = await FRAuth.next(step, { tree });
        setStep(nextstep);
        setIsPollWaitTimerRegistered(false);
      }, (callback as PollingWaitCallback).getWaitTime());
      setIsPollWaitTimerRegistered(true);
    } else {
      console.debug("pollwait timer already registered");
    }

    return <></>;
   
  }

/***This function creates the array of form elements based
 *  on the callbacks recieved from forgerock journey call */
const getUIElements = () => {
  let finalChildren: JSX.Element[] = [];
  const metaInfoMap: Map<any, JSX.Element[]> = new Map();
  if (!!step) {
    step.callbacks.forEach((callback) => {
      if(callback.getType() === CallbackType.PollingWaitCallback){
        finalChildren.push(handlePollingWaitCallBack(callback as PollingWaitCallback));
      }else if (viewableCallBackTypes.indexOf(callback.getType()) > -1){
        const attributeInputCallback = new AttributeInputCallback(callback.payload);
        const localMetaDataMap = cloneDeepWith(metaDataMap)
        const attributeName =
          attributeInputCallback.getType() === CallbackType.TermsAndConditionsCallback ? "terms" : attributeInputCallback.getName();
        const attributeWiseMapData = metaDataMap.get(attributeName);
        const mapData: any = metaInfoMap.get(attributeWiseMapData?.header) ? metaInfoMap.get(attributeWiseMapData?.header) : [];

        const attributeMetaDataValue = metaDataMap.get(attributeInputCallback.getName());
        if (attributeMetaDataValue?.addOnClickFunction && onClickFunctionMap?.get(attributeInputCallback.getName())) {
          localMetaDataMap.set(attributeInputCallback.getName(), { ...attributeMetaDataValue, onClick: onClickFunctionMap?.get(attributeInputCallback.getName()) })
        }
        mapData.push(getUiElementBasedOnCallBack(attributeInputCallback, localMetaDataMap))
        metaInfoMap.set(attributeWiseMapData?.header, mapData);
      }
    })

    finalChildren.push(<>{getUIElementsFromMap(metaInfoMap)}</>);
  }

  return finalChildren;
}


const createBottomElement = (metaDataMap?: Map<string, any>) => {
  let bottomElement: JSX.Element | undefined;

  if (!!step) {
    const confirmationCallbacks = step.getCallbacksOfType(
      CallbackType.ConfirmationCallback
    );

    if (metaDataMap?.get("isSubmitVisible")?.value === false || isSubmitHidden === true) {
      bottomElement = <></>;
    } else if (confirmationCallbacks?.length) {
      const options = confirmationCallbacks[0].getOutputValue("options") as string[];
      const styles = metaDataMap?.get("$confirmationCallBack")?.callbacks.map(
        (callback: { style: any; }) => callback.style ?? {}
      );

      bottomElement = (
        <div className={styles?.bottom}>
          {metaDataMap?.get("$confirmationCallBack")?.requiredLabel && (
            <span className={styles?.requiredLabel}>* Required field</span>
          )}
          <div style={{ width: "100%" }}>
            {options.map((option, index) => (
              <button
                key={index}
                type="submit"
                disabled={isDisabled}
                style={styles?.[index]}
                className={'submitButton'}
              >
                {t(option)}
              </button>
            ))}
          </div>
        </div>
      );
    }
  }

  setBottomElement(bottomElement);
};


/**Function handle the output result from the form and makes call to next step in journey */
const handleSuccess = async (data: any) => {
  isLoaderVisible(true)
  setDisabled(true)
  let filledStep = fillStep(step as FRStep, data, metaDataMap);
  setMessage ? setMessage("") : null
  setIsAlertOpen ? setIsAlertOpen(false) : null
  let nextStep = await FRAuth.next(filledStep as FRStep, { tree });
  setDisabled(false);
   isLoaderVisible(false)
  setStep(nextStep);
}

const getSubHeaderText = () => step.getDescription()?.trim() || t(getTextOutputCallBackByMessageType(step, "0")?.getOutputByName('message', 'prompt.otpEmail'), metaDataMap.get("typeBasedCallbacks")?.textOutputCallback?.translationData);

return (
  <> <Loader visible={loaderVisible}/>
  <div className={styles.container}>
    {setIsAlertOpen && isAlertOpen ? <Notification
      open={isAlertOpen}
      handleClose={() => setIsAlertOpen(false)}
      type={messageType}
      message={message}
    /> : <></>}

   { step &&<CustomFormContainer formContext={formContext} onSuccess={async (data) => {
      setDisabled(true)
      await handleSuccess(data)
      setDisabled(false)
    }}>
      <FormLayout
        header={step.getHeader()}
        subHeader={getSubHeaderText()}
        backButton={backBottomElement}
        submitButton={bottomElement}
        backLink={backLink}
        disabled={isDisabled}
        additionBottomElement={additionalBottomElement}
      >
        <>
          {getUIElements()}
        </>
      </FormLayout>
    </CustomFormContainer>}
  </div> 
  </>
);
}

export default CallbasedViewGenerator;